import { useForm } from 'react-hook-form';

export interface InvoiceCompany {
  HSN?: string;
  gst_rate?: string;
  company_name: string;
  PAN: string;
  invoice_title?: string;
  company_gstin: string;
  STATE?: string;
  STATE_CODE?: string;
  SAC_CODE?: string;
  DB_STATE_ID?: number;
  address?: string;
  address_2?: string;
  mobile?: string;
  email?: string;
  bank_account_name?: string;
  bank_name?: string;
  bank_account_no?: string;
  ifsc_code?: string;
  branch?: string;
  upi_id?: string;
  terms?: string;
  isActive?: boolean;
  logo_url?: string;
  stamp_url?: string;
  signature_url?: string;
}

interface Props {
  defaultValues?: Partial<InvoiceCompany>;
  onSubmit: (v: InvoiceCompany) => void;
  onCancel: () => void;
}

export function InvoiceCompanyForm({ defaultValues, onSubmit, onCancel }: Props) {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<InvoiceCompany>({
    defaultValues: {
      HSN: '996812', gst_rate: '18%', SAC_CODE: '996812', invoice_title: 'Freight Charges',
      isActive: true,
      ...defaultValues,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <Section title="Company Identity">
        <div className="grid grid-cols-2 gap-3">
          <Field label="GSTIN *" error={errors.company_gstin?.message}>
            <input {...register('company_gstin', { required: 'Required' })} placeholder="22AAAAA0000A1Z5" className="input font-mono uppercase" />
          </Field>
          <Field label="Company Name *" error={errors.company_name?.message}>
            <input {...register('company_name', { required: 'Required' })} placeholder="SHIPROUTE LOGISTICS PVT LTD" className="input" />
          </Field>
          <Field label="PAN *" error={errors.PAN?.message}>
            <input {...register('PAN', { required: 'Required' })} placeholder="ABCDE1234F" className="input font-mono uppercase" />
          </Field>
          <Field label="Invoice Title">
            <input {...register('invoice_title')} placeholder="Freight Charges" className="input" />
          </Field>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Company Logo (printed on the invoice PDF)</label>
          {watch('logo_url') ? (
            <div className="flex items-center gap-3 mb-2">
              <img src={watch('logo_url')} alt="logo" style={{ maxHeight: 80, maxWidth: 200, border: '1px solid #e5e7eb', borderRadius: 4, padding: 4 }} />
              <button type="button" onClick={() => setValue('logo_url', '', { shouldDirty: true })} className="text-xs text-danger hover:underline">Remove</button>
            </div>
          ) : (
            <div className="text-xs text-slate-400 mb-2">No logo uploaded yet</div>
          )}
          <input type="hidden" {...register('logo_url')} />
          <input
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 1_200_000) { alert('Logo too large (max 1.2 MB)'); return; }
              const reader = new FileReader();
              reader.onload = () => setValue('logo_url', reader.result as string, { shouldDirty: true });
              reader.readAsDataURL(file);
            }}
            className="text-xs"
          />
          <p className="text-xs text-slate-400 mt-1">Recommended: 220 × 220 px transparent PNG. Max 1.2 MB.</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          {/* ── Stamp image ── */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Company Stamp (printed on invoice + credit note PDFs)</label>
            {watch('stamp_url') ? (
              <div className="flex items-center gap-3 mb-2">
                <img src={watch('stamp_url')} alt="stamp" style={{ maxHeight: 80, maxWidth: 200, border: '1px solid #e5e7eb', borderRadius: 4, padding: 4, background: '#fafafa' }} />
                <button type="button" onClick={() => setValue('stamp_url', '', { shouldDirty: true })} className="text-xs text-danger hover:underline">Remove</button>
              </div>
            ) : (
              <div className="text-xs text-slate-400 mb-2">No stamp uploaded yet</div>
            )}
            <input type="hidden" {...register('stamp_url')} />
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 1_200_000) { alert('Stamp too large (max 1.2 MB)'); return; }
                const reader = new FileReader();
                reader.onload = () => setValue('stamp_url', reader.result as string, { shouldDirty: true });
                reader.readAsDataURL(file);
              }}
              className="text-xs"
            />
            <p className="text-xs text-slate-400 mt-1">Round/square company stamp. Transparent PNG preferred.</p>
          </div>

          {/* ── Signature image ── */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Authorised Signature (printed in signatory block)</label>
            {watch('signature_url') ? (
              <div className="flex items-center gap-3 mb-2">
                <img src={watch('signature_url')} alt="signature" style={{ maxHeight: 80, maxWidth: 200, border: '1px solid #e5e7eb', borderRadius: 4, padding: 4, background: '#fafafa' }} />
                <button type="button" onClick={() => setValue('signature_url', '', { shouldDirty: true })} className="text-xs text-danger hover:underline">Remove</button>
              </div>
            ) : (
              <div className="text-xs text-slate-400 mb-2">No signature uploaded yet</div>
            )}
            <input type="hidden" {...register('signature_url')} />
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 1_200_000) { alert('Signature too large (max 1.2 MB)'); return; }
                const reader = new FileReader();
                reader.onload = () => setValue('signature_url', reader.result as string, { shouldDirty: true });
                reader.readAsDataURL(file);
              }}
              className="text-xs"
            />
            <p className="text-xs text-slate-400 mt-1">Signature scan. Transparent PNG preferred, ~300×120 px.</p>
          </div>
        </div>
      </Section>

      <Section title="Tax / Code Defaults">
        <div className="grid grid-cols-4 gap-3">
          <Field label="HSN"><input {...register('HSN')} className="input font-mono" /></Field>
          <Field label="SAC Code"><input {...register('SAC_CODE')} className="input font-mono" /></Field>
          <Field label="GST Rate"><input {...register('gst_rate')} placeholder="18%" className="input" /></Field>
          <Field label="State Code">
            <input {...register('STATE_CODE')} placeholder="09" className="input font-mono" />
          </Field>
        </div>
      </Section>

      <Section title="Registered Address">
        <div className="grid grid-cols-3 gap-3">
          <Field label="State">
            <input {...register('STATE')} placeholder="UTTAR PRADESH" className="input" />
          </Field>
          <Field label="DB State ID">
            <input type="number" {...register('DB_STATE_ID', { valueAsNumber: true })} className="input" />
          </Field>
          <Field label="Mobile">
            <input {...register('mobile')} className="input" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Address Line 1">
            <input {...register('address')} placeholder="Building, street, area" className="input" />
          </Field>
          <Field label="Address Line 2">
            <input {...register('address_2')} placeholder="Noida-201301" className="input" />
          </Field>
        </div>
        <Field label="Email">
          <input type="email" {...register('email')} placeholder="billing@example.com" className="input" />
        </Field>
      </Section>

      <Section title="Bank (used on invoice footer)">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Account Holder Name">
            <input {...register('bank_account_name')} className="input" />
          </Field>
          <Field label="Bank Name">
            <input {...register('bank_name')} className="input" />
          </Field>
          <Field label="Account Number">
            <input {...register('bank_account_no')} className="input font-mono" />
          </Field>
          <Field label="IFSC Code">
            <input {...register('ifsc_code')} className="input font-mono uppercase" />
          </Field>
          <Field label="UPI ID"><input {...register('upi_id')} placeholder="company@upi" className="input" />
          </Field>
        </div>
        <Field label="Branch">
          <input {...register('branch')} className="input" />
        </Field>
      </Section>

      <Section title="Invoice Terms (printed at the bottom)">
        <textarea {...register('terms')} rows={6} className="input text-xs font-mono"
          placeholder={"1. Please pay cheque / draft in favour of ...\n2. Payment within 7 days.\n3. Late payment interest 2% per month.\n4. Disputes subject to ... Jurisdiction."} />
      </Section>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100 sticky bottom-0 bg-white pb-2">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" {...register('isActive')} className="rounded" />
          Active (available in invoice dropdowns)
        </label>
        <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700">
          Save GST / Billing Company
        </button>
        </div>
      </div>
    </form>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
